import React from 'react'
import styled from 'styled-components'
import { Flex, Box } from 'rebass'
import { Heading } from 'rebass'
import {
  Badge,
  media,
  MediumScreenUp,
  SmallScreenOnly
} from '../../utils/style'
import { navigate } from 'gatsby'
import IconArrowRight from '../../vectors/IconArrowRight'
import IconArrowLeft from '../../vectors/IconArrowLeft'
import buildImageUrl from '../../../lib/media-url-maker'

const PostsFrame = styled(Flex)`
  max-width: ${(props) => props.theme.content.max.width};
  position: relative;
  z-index: 1;
  a {
    text-decoration: none;
  }
`

const NextPrevBoc = styled(Box)`
  box-shadow: 0 50px 8px 0 rgba(102, 102, 102, 0.01),
    0 20px 20px 0 rgba(0, 0, 0, 0.01);
  cursor: pointer;
  position: relative;
  background-color: white;
  line-height: 1;
  transition: color 1.5s ${(props) => props.theme.ease.ease_out_expo};
  ${media.MediumScreenUp`
    min-height: 230px;
    &::after {
      content: '';
      position: absolute;
      z-index: 1;
      top: 0;
      left: 0;
      width: 40px;
      height: 100%;
      background-color: ${(props) => props.theme.colors.grey_light_op30};
      transition: background-color 1.5s ${(props) =>
        props.theme.ease.ease_out_expo}, transform 0.5s ${(props) =>
    props.theme.ease.ease_out_expo};
      transform-origin: 0 50%;
    }
  `};

  &:hover {
    &::after {
      transform: scaleX(1.06);
    }
    & svg {
      transform: translateX(-3px) translateY(-50%);
    }
  }

  svg {
    position: absolute;
    top: 50%;
    z-index: 2;
    transform: translateY(-50%);
    transition: transform 0.5s ${(props) => props.theme.ease.ease_out_expo};
  }
`

const NextFrame = styled(NextPrevBoc)`
  background-size: cover;
  background-repeat: no-repeat;
  background-position: top center;
  background-color: ${(props) => props.theme.colors.grey};
  background-image: url(${(props) => props.bg}.jpg);

  @media only screen and (-webkit-min-device-pixel-ratio: 1.3),
    only screen and (-o-min-device-pixel-ratio: 1.3/1),
    only screen and (min-resolution: 144dpi),
    only screen and (min-resolution: 1.5dppx) : {
    background-image: url(${(props) =>
      buildImageUrl({
        width: 600,
        resource: props.bg
      })});
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    opacity: 0.65;
    background-color: ${(props) => props.theme.colors.grey};
    transition: opacity 2.8s ${(props) => props.theme.ease.ease_out_expo};
  }
  &:hover::before {
    opacity: 0.4;
  }
  &::after {
    transform-origin: 100% 50%;
    left: auto;
    right: 0;
    background-color: ${(props) => props.theme.colors.blue};
  }
  svg {
    right: 10px;
  }
`

const PreviousFrame = styled(NextPrevBoc)`
  color: ${(props) => props.theme.colors.grey_light};

  &:hover {
    background-color: white;
    color: ${(props) => props.theme.colors.grey};
  }

  &:hover {
    & svg {
      transform: translateX(3px) translateY(-50%);
    }
    &::after {
      background-color: ${(props) => props.theme.colors.grey_light_op65};
    }
  }

  svg {
    left: 10px;
  }
`

const Title = styled(Heading)`
  text-transform: uppercase;
  letter-spacing: 0.1em;
  position: absolute;
  z-index: 1;
  right: 20px;
  top: 20px;
  ${media.MediumScreenUp`
    position: relative;
    right: auto;
    top: auto;
  `};
`

const NextTitle = styled(Title)`
  color: ${(props) => props.theme.colors.white_op65};
`
const PrevTitle = styled(Title)`
  color: ${(props) => props.theme.colors.blue};
`

const ArticleTitle = styled(Heading)`
  position: relative;
  z-index: 1;
  color: white;
`

const Category = styled(Badge)`
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  line-height: 1.1;
  position: absolute;
  z-index: 1;
  top: 20px;
  left: 25px;
`

class ContentArticleNextPrevPosts extends React.PureComponent {
  render() {
    return (
      <PostsFrame flexWrap="wrap" as="nav" my={[40, 100]} mx={['8%', 0]}>
        {this.props.next && (
          <NextFrame
            order={[1, 2]}
            width={[1 / 1, 1 / 2]}
            pt={[60, 70]}
            pb={[50, 80]}
            pr={[15, 80]}
            pl={[25, 60]}
            bg={this.props.next.image}
            onClick={() => navigate(this.props.next.path)}
          >
            <NextTitle as="h6" fontSize={[0]} mb={[10]}>
              Next Article
            </NextTitle>
            {this.props.next.category && (
              <SmallScreenOnly>
                <Category ml={'0px'} px={'10px'} py={'4px'}>
                  {this.props.next.category}
                </Category>
              </SmallScreenOnly>
            )}
            <ArticleTitle as="span" fontSize={[3, 7]}>
              {this.props.next.shortHeadline}
            </ArticleTitle>
            <MediumScreenUp>
              <IconArrowRight />
            </MediumScreenUp>
          </NextFrame>
        )}
        {this.props.prev && (
          <PreviousFrame
            order={[2, 1]}
            width={[1 / 1, 1 / 2]}
            pt={[60, 70]}
            pb={[50, 80]}
            pl={[25, 80]}
            pr={[15, 60]}
            onClick={() => navigate(this.props.prev.path)}
          >
            <PrevTitle as="h6" fontSize={[0]} mb={[10]}>
              Previous Article
            </PrevTitle>
            {this.props.prev.category && (
              <SmallScreenOnly>
                <Category ml={'0px'} px={'10px'} py={'4px'}>
                  {this.props.prev.category}
                </Category>
              </SmallScreenOnly>
            )}
            <Heading as="span" fontSize={[3, 7]}>
              {this.props.prev.shortHeadline}
            </Heading>
            <MediumScreenUp>
              <IconArrowLeft />
            </MediumScreenUp>
          </PreviousFrame>
        )}
      </PostsFrame>
    )
  }
}

export default ContentArticleNextPrevPosts
